import { normalizeHeaderImages, revokeLocalImages } from './localImage.js';
import { normalizeHeaderFiles, revokeLocalFiles } from './localFile.js';
import { normalizeHeaderAudios, revokeLocalAudios } from './localAudio.js';
import { normalizeHeaderVideos, revokeLocalVideos } from './localVideo.js';
import {
	normalizeHeaderCarouselData,
	revokeHeaderCarouselImages,
	HEADER_CAROUSEL_MODULE_TYPE,
	HEADER_CAROUSEL_VARIANT_BANNER,
} from './headerCarousel.js';
import {
	normalizeHeaderHeroData,
	revokeHeaderHeroImages,
	HEADER_HERO_MODULE_TYPE,
} from './headerHero.js';
import {
	normalizeHeaderInfoCardData,
	revokeHeaderInfoCardImages,
	HEADER_INFO_CARD_MODULE_TYPE,
} from './headerInfoCard.js';
import {
	normalizeHeaderInfoCardV2Data,
	revokeHeaderInfoCardV2Images,
	HEADER_INFO_CARD_V2_MODULE_TYPE,
} from './headerInfoCardV2.js';
import {
	normalizeHeaderIpBrandData,
	revokeHeaderIpBrandImages,
	HEADER_IP_BRAND_MODULE_TYPE,
} from './headerIpBrand.js';

export const HEADER_MODULE_TYPES = {
	image: 'image',
	audio: 'audio',
	video: 'video',
	file: 'file',
	carousel: HEADER_CAROUSEL_MODULE_TYPE,
	hero: HEADER_HERO_MODULE_TYPE,
	infoCard: HEADER_INFO_CARD_MODULE_TYPE,
	infoCardV2: HEADER_INFO_CARD_V2_MODULE_TYPE,
	ipBrand: HEADER_IP_BRAND_MODULE_TYPE,
};

const HEADER_MODULE_META = {
	image: { label: '图片模块', editLabel: '图片设置' },
	audio: { label: '音频模块', editLabel: '音频设置' },
	video: { label: '视频模块', editLabel: '视频设置' },
	file: { label: '文件模块', editLabel: '文件设置' },
	carousel: { label: '头部轮播', editLabel: '头部模块编辑' },
	hero: { label: '头部展示', editLabel: '头部模块编辑' },
	infoCard: { label: '图文头部', editLabel: '头部模块编辑' },
	infoCardV2: { label: '图文卡片头部', editLabel: '头部模块编辑' },
	ipBrand: { label: 'IP品牌头部', editLabel: '头部模块编辑' },
};

export function createDefaultHeaderModule() {
	return {
		type: null,
		data: null,
	};
}

export function getHeaderModuleMeta(type) {
	return HEADER_MODULE_META[type] || { label: '头部模块', editLabel: '设置' };
}

export function hasHeaderModuleContent(module) {
	if (!module?.type || !module.data) {
		return false;
	}
	switch (module.type) {
		case HEADER_MODULE_TYPES.image: {
			const imageItems = module.data.items ?? module.data.images;
			return Array.isArray(imageItems) && imageItems.length > 0;
		}
		case HEADER_MODULE_TYPES.audio:
		case HEADER_MODULE_TYPES.video:
		case HEADER_MODULE_TYPES.file:
			return Array.isArray(module.data.items) && module.data.items.length > 0;
		case HEADER_MODULE_TYPES.carousel: {
			const carouselItems = module.data.items ?? module.data.slides;
			return Array.isArray(carouselItems) && carouselItems.length > 0;
		}
		case HEADER_MODULE_TYPES.hero:
			return !!(module.data.title || module.data.subtitle || module.data.buttonText);
		case HEADER_MODULE_TYPES.infoCard:
			return !!(module.data.title || module.data.subtitle || module.data.image);
		case HEADER_MODULE_TYPES.infoCardV2: {
			const items = module.data.items ?? module.data.slides;
			if (Array.isArray(items) && items.length > 0) return true;
			return !!(module.data.title || module.data.subtitle || module.data.image);
		}
		case HEADER_MODULE_TYPES.ipBrand:
			return !!(module.data.title || module.data.subtitle || module.data.badge || module.data.image);
		default:
			return false;
	}
}

/**
 * 贴顶铺满布局：外层不再需要顶部与两侧 margin/padding
 * （banner 轮播、hero 展示头）
 */
export function isHeaderModuleTopBleed(module) {
	if (!module?.type || !module.data) {
		return false;
	}
	if (module.type === HEADER_MODULE_TYPES.hero) {
		return true;
	}
	if (module.type === HEADER_MODULE_TYPES.ipBrand) {
		return true;
	}
	if (module.type === HEADER_MODULE_TYPES.infoCardV2) {
		return true;
	}
	if (module.type === HEADER_MODULE_TYPES.carousel) {
		return module.data.variant === HEADER_CAROUSEL_VARIANT_BANNER;
	}
	return false;
}

export function normalizeHeaderModule(module = createDefaultHeaderModule()) {
	if (!hasHeaderModuleContent(module)) {
		return createDefaultHeaderModule();
	}
	if (module.type === HEADER_MODULE_TYPES.image) {
		return {
			type: module.type,
			data: normalizeHeaderImages(module.data),
		};
	}
	if (module.type === HEADER_MODULE_TYPES.file) {
		return {
			type: module.type,
			data: normalizeHeaderFiles(module.data),
		};
	}
	if (module.type === HEADER_MODULE_TYPES.audio) {
		return {
			type: module.type,
			data: normalizeHeaderAudios(module.data),
		};
	}
	if (module.type === HEADER_MODULE_TYPES.video) {
		return {
			type: module.type,
			data: normalizeHeaderVideos(module.data),
		};
	}
	if (module.type === HEADER_MODULE_TYPES.carousel) {
		return {
			type: module.type,
			data: normalizeHeaderCarouselData(module.data),
		};
	}
	if (module.type === HEADER_MODULE_TYPES.hero) {
		return {
			type: module.type,
			data: normalizeHeaderHeroData(module.data),
		};
	}
	if (module.type === HEADER_MODULE_TYPES.infoCard) {
		return {
			type: module.type,
			data: normalizeHeaderInfoCardData(module.data),
		};
	}
	if (module.type === HEADER_MODULE_TYPES.infoCardV2) {
		return {
			type: module.type,
			data: normalizeHeaderInfoCardV2Data(module.data),
		};
	}
	if (module.type === HEADER_MODULE_TYPES.ipBrand) {
		return {
			type: module.type,
			data: normalizeHeaderIpBrandData(module.data),
		};
	}
	return createDefaultHeaderModule();
}

export function clearHeaderModule(module) {
	if (!module?.type || !module.data) {
		return;
	}
	if (module.type === HEADER_MODULE_TYPES.image) {
		revokeLocalImages(module.data.items ?? module.data.images);
	}
	if (module.type === HEADER_MODULE_TYPES.file) {
		revokeLocalFiles(module.data.items);
	}
	if (module.type === HEADER_MODULE_TYPES.audio) {
		revokeLocalAudios(module.data.items);
	}
	if (module.type === HEADER_MODULE_TYPES.video) {
		revokeLocalVideos(module.data.items);
	}
	if (module.type === HEADER_MODULE_TYPES.carousel) {
		revokeHeaderCarouselImages(module.data);
	}
	if (module.type === HEADER_MODULE_TYPES.hero) {
		revokeHeaderHeroImages(module.data);
	}
	if (module.type === HEADER_MODULE_TYPES.infoCard) {
		revokeHeaderInfoCardImages(module.data);
	}
	if (module.type === HEADER_MODULE_TYPES.infoCardV2) {
		revokeHeaderInfoCardV2Images(module.data);
	}
	if (module.type === HEADER_MODULE_TYPES.ipBrand) {
		revokeHeaderIpBrandImages(module.data);
	}
}
